/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Widget Subscriber Mobile Component
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.20.0
 * @since 1.0.1
 */
import { Fragment, memo, useEffect } from 'react';

import map from 'lodash/map';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import { icons } from '../../../../../../assets';
import { Skeleton } from '../../../../../../components/common';
import { FlexColumnStyled, FlexStyled } from '../../../../../../components/common/Block/Flexbox';
import { ImgStyled } from '../../../../../../components/common/Block/Img';
import { LayoutStyled } from '../../../../../../components/common/Block/Layout';
import Tab from '../../../../../../components/common/Tabs/Tab';
import Tabs from '../../../../../../components/common/Tabs/Tabs';
import ArrowTooltip from '../../../../../../components/common/Tooltip/Tooltip';
import { dropTableFilterAndSort } from '../../../../../../components/Table/actions/tableActions';
import { IDropTableFilterAndSortPayload } from '../../../../../../components/Table/types/tableTypes';
import { pushGTMAnalytic } from '../../../../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { EGTMWidgetSubscriberMobileLabel } from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetSubscriberMobileOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { appHistoryPush } from '../../../../../../modules/app/appHistory';
import initTranslationCommon from '../../../../../../modules/internationalization/constants/common';
import initTranslationPageDashboard
    from '../../../../../../modules/internationalization/constants/pages/dashboard/dashboard';
import { IPageDashboardTranslation } from '../../../../../../modules/internationalization/types';
import ICommonTranslation from '../../../../../../modules/internationalization/types/common';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { useSetTableFilterAndSortMutation } from '../../../../../../modules/table/store';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { RoutePath } from '../../../../../../routes/constants/routesConstants';
import { utoa } from '../../../../../../utils/URIEncode';
import { makeSelectAuthoritiesIsAssistant } from '../../../../../PageAuth/selectors/authSelectors';
import { API_SUBSCRIBER_TABLE_FILTER_AND_SORT } from '../../../../../PageSubscriberMobile';
import { WidgetSkeletonParagraph } from '../../constants/widgetSkeletonContants';
import { WidgetTitleStyled } from '../../styled/WidgetStyled';
import WidgetError from '../../WidgetError';
import {
    EWidgetSearchAccountsField,
    IWidgetSearchAccountsItem,
} from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { EWidgetStatus } from '../../widgetTypes';
import { WidgetSubscriberMobileStyled } from '../styled/widgetSubscriberMobileStyled';
import { widgetSubscriberMobileRequest } from '../widgetSubscriberMobileActions';
import {
    makeSelectorLabelsStatus,
    makeSelectorTariffsStatus,
    makeSelectWidgetSubscriberMobileGetActiveSubs,
    makeSelectWidgetSubscriberMobileGetBlockedSubs,
    makeSelectWidgetSubscriberMobileGetLabels,
    makeSelectWidgetSubscriberMobileGetNoSimCardSubs,
    makeSelectWidgetSubscriberMobileGetTariffs,
    makeSelectWidgetSubscriberMobileIsLoading,
} from '../widgetSubscriberMobileSelects';
import {
    EWidgetSubscriberMobile,
    IWidgetSubscriberMobileItem,
    IWidgetSubscriberMobileStatusSubs,
} from '../widgetSubscriberMobileTypes';
import WidgetSubscriberMobileEmpty from './WidgetSubscriberMobileEmpty';
import WidgetSubscriberMobileSuccessful from './WidgetSubscriberMobileSuccessful';

interface IConnectedProps {
    activeSubs: IWidgetSubscriberMobileStatusSubs;
    blockedSubs: IWidgetSubscriberMobileStatusSubs;
    isAssistant: boolean;
    labels: IWidgetSubscriberMobileItem[];
    loading: boolean;
    noSimCardSubs: IWidgetSubscriberMobileStatusSubs;
    selectedAccount: IWidgetSearchAccountsItem;
    statusLabels: EWidgetStatus;
    statusTariffs: EWidgetStatus;
    tariffs: IWidgetSubscriberMobileItem[];
}

interface IDispatchProps {
    dropFilterAndSort: (payload: IDropTableFilterAndSortPayload) => void;
    loadData: () => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

interface IWidgetSubscriberMobileProps {
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetSubscriberMobile = IWidgetSubscriberMobileProps & IConnectedProps & IDispatchProps;

export const WidgetSubscriberMobile = (props: TWidgetSubscriberMobile) => {

    const {
        activeSubs,
        blockedSubs,
        noSimCardSubs,
        labels,
        loadData,
        loading,
        statusLabels,
        statusTariffs,
        tariffs,
        translation,
    } = props;

    useEffect(() => {
        loadData();
    }, [loadData, props.selectedAccount.id]);

    const [setTableFilterAndSort] = useSetTableFilterAndSortMutation();

    const isCustomRoleCheckAvailable = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_TAGS_IMPORT);

    const onClickStatus = async (status: string[]) => {
        let tabName: EGTMWidgetSubscriberMobileLabel;
        switch (status) {
            case activeSubs.names:
                tabName = EGTMWidgetSubscriberMobileLabel.activeSubs;
                break;
            case blockedSubs.names:
                tabName = EGTMWidgetSubscriberMobileLabel.blockedSubs;
                break;
            case noSimCardSubs.names:
                tabName = EGTMWidgetSubscriberMobileLabel.noSimCardSubs;
                break;
            default:
                tabName = EGTMWidgetSubscriberMobileLabel.activeSubs;
                break;
        }
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetSubscriberMobile.clickLinkLabelWidgetSubscriberMobile(tabName),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });

        const accountNumber = props.selectedAccount[EWidgetSearchAccountsField.number];
        const filter = {
            status: { value: map(status, (name: string) => utoa(name)) },
            ...(accountNumber
                // если у пользователя несколько ЛС
                ? { account: { value: [utoa(accountNumber)] } }
                : {}),
        };

        setTableFilterAndSort({
            url: API_SUBSCRIBER_TABLE_FILTER_AND_SORT,
            filter,
        }).then(() => {
            appHistoryPush(RoutePath.subscriberMobile);
        });
    };

    const onLabelTabClickHandler = (): void => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetSubscriberMobile
                .clickTabWidgetSubscriberMobile(EWidgetSubscriberMobile.LABELS),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const onTariffTabClickHandler = (): void => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetSubscriberMobile
                .clickTabWidgetSubscriberMobile(EWidgetSubscriberMobile.TARIFFS),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    return (
        <FlexColumnStyled>
            <WidgetSubscriberMobileStyled data-widget="WidgetSubscriberMobile">
                <LayoutStyled className="mb-3">
                    <WidgetTitleStyled>
                        {translation.widgets.subscriberMobile.title}
                    </WidgetTitleStyled>
                </LayoutStyled>
                {
                    (activeSubs.count > 0 || blockedSubs.count > 0 || noSimCardSubs.count > 0) &&
                    <FlexStyled className="mb-3">
                        <Skeleton
                            count={1}
                            isLoading={loading}
                            render={() => WidgetSkeletonParagraph}>
                            <Fragment>
                                {
                                    activeSubs.count > 0 &&
                                    <FlexStyled
                                        className="widget-subscriber-mobile__count-active-subs mr-3"
                                        cursor="pointer"
                                        data-button="onClickStatusActiveSubs"
                                        justifyContent="space-between"
                                        onClick={() => onClickStatus(activeSubs.names)}
                                    >
                                        <ArrowTooltip
                                            dataName="toolTipStatusActiveSubs"
                                            title={translation.widgets.subscriberMobile.activeSubs}>
                                            <LayoutStyled className="mr-1">
                                                <ImgStyled
                                                    className="small"
                                                    src={icons.activeSubs}
                                                />
                                            </LayoutStyled>
                                        </ArrowTooltip>
                                        {activeSubs.count}
                                    </FlexStyled>
                                }

                                {
                                    blockedSubs.count > 0 &&
                                    <FlexStyled
                                        className="widget-subscriber-mobile__count-block-subs mr-3"
                                        cursor="pointer"
                                        data-button="onClickStatusBlockedSubs"
                                        justifyContent="space-between"
                                        onClick={() => onClickStatus(blockedSubs.names)}
                                    >
                                        <ArrowTooltip
                                            dataName="toolTipStatusBlockedSubs"
                                            title={translation.widgets.subscriberMobile.blockedSubs}>
                                            <LayoutStyled className="mr-1">
                                                <ImgStyled
                                                    className="small"
                                                    src={icons.blockSubs}/>
                                            </LayoutStyled>
                                        </ArrowTooltip>
                                        {blockedSubs.count}
                                    </FlexStyled>
                                }

                                {
                                    noSimCardSubs.count > 0 &&
                                    <FlexStyled
                                        className="widget-subscriber-mobile__count-no-sim mr-3"
                                        cursor="pointer"
                                        data-button="onClickStatusNoSimCardSubs"
                                        justifyContent="space-between"
                                        onClick={() => onClickStatus(noSimCardSubs.names)}
                                    >
                                        <ArrowTooltip
                                            dataName="toolTipStatusNoSimCardSubs"
                                            title={translation.widgets.subscriberMobile.noSimCard}>
                                            <LayoutStyled className="mr-1">
                                                <ImgStyled
                                                    className="small"
                                                    src={icons.noSim}/>
                                            </LayoutStyled>
                                        </ArrowTooltip>
                                        {noSimCardSubs.count}
                                    </FlexStyled>
                                }
                            </Fragment>
                        </Skeleton>
                    </FlexStyled>
                }
                <Tabs>
                    <Tab
                        className="widget-subscriber-mobile__tab-tariffs"
                        label={translation.widgets.subscriberMobile.tariffs.label}
                        onClick={onTariffTabClickHandler}
                    >
                        <Skeleton
                            count={3}
                            isLoading={loading}
                            render={() => WidgetSkeletonParagraph}>
                            <Fragment>
                                {{
                                    empty: <WidgetSubscriberMobileEmpty
                                        className="widget-subscriber-mobile__empty-tariff"
                                        pushGtmAnalytic={props.pushGtmAnalytic}
                                        translation={translation}
                                        type={EWidgetSubscriberMobile.TARIFFS}
                                    />,
                                    error: <WidgetError
                                        className="widget-subscriber-mobile__error-tariff"
                                        text={translation.widgets.subscriberMobile.fail.text}
                                    />,
                                    success: <WidgetSubscriberMobileSuccessful
                                        className="widget-subscriber-mobile__success-tariff"
                                        field={EWidgetSubscriberMobile.TARIFFS}
                                        items={tariffs}
                                        pushGtmAnalytic={props.pushGtmAnalytic}
                                        translation={translation.widgets.subscriberMobile.successful.tariff}
                                    />,
                                }[statusTariffs]}
                            </Fragment>
                        </Skeleton>
                    </Tab>
                    <Tab
                        className="widget-subscriber-mobile__tab-labels"
                        label={translation.widgets.subscriberMobile.labels.label}
                        onClick={onLabelTabClickHandler}
                    >
                        <Skeleton
                            count={3}
                            isLoading={loading}
                            render={() => WidgetSkeletonParagraph}>
                            <Fragment>
                                {{
                                    empty: <WidgetSubscriberMobileEmpty
                                        className="widget-subscriber-mobile__empty-labels"
                                        isAllowToManageLabels={!props.isAssistant || isCustomRoleCheckAvailable}
                                        pushGtmAnalytic={props.pushGtmAnalytic}
                                        translation={translation}
                                        type={EWidgetSubscriberMobile.LABELS}
                                    />,
                                    error: <WidgetError
                                        className="widget-subscriber-mobile__error-labels"
                                        text={translation.widgets.subscriberMobile.fail.text}
                                    />,
                                    success: <WidgetSubscriberMobileSuccessful
                                        className="widget-subscriber-mobile__success-labels"
                                        field={EWidgetSubscriberMobile.LABELS}
                                        items={labels}
                                        pushGtmAnalytic={props.pushGtmAnalytic}
                                        translation={translation.widgets.subscriberMobile.successful.labels}
                                    />,
                                }[statusLabels]}
                            </Fragment>
                        </Skeleton>
                    </Tab>
                </Tabs>
            </WidgetSubscriberMobileStyled>
        </FlexColumnStyled>
    );
};

WidgetSubscriberMobile.defaultProps = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon },
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    activeSubs: makeSelectWidgetSubscriberMobileGetActiveSubs(),
    blockedSubs: makeSelectWidgetSubscriberMobileGetBlockedSubs(),
    isAssistant: makeSelectAuthoritiesIsAssistant(),
    labels: makeSelectWidgetSubscriberMobileGetLabels(),
    loading: makeSelectWidgetSubscriberMobileIsLoading(),
    noSimCardSubs: makeSelectWidgetSubscriberMobileGetNoSimCardSubs(),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
    statusLabels: makeSelectorLabelsStatus(),
    statusTariffs: makeSelectorTariffsStatus(),
    tariffs: makeSelectWidgetSubscriberMobileGetTariffs(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    dropFilterAndSort: (payload) => dispatch(dropTableFilterAndSort(payload)),
    loadData: () => dispatch(widgetSubscriberMobileRequest()),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(WidgetSubscriberMobile));
